import React from 'react';
import {useTranslation} from 'react-i18next';
import {seoLinks} from '../SeoLinks';
import config from '../../config';
import {useLocation} from 'react-router';
import {socialImage, socialLocales} from '../../social';
import {DEFAULT_LANGUAGE, LANGUAGES} from '../../languages';

export const SocialMeta = ({
    title,
    description,
    canonical,
    type = 'website',
    image = socialImage,
    alternateLanguages = [],
}) => {
    const {pathname} = useLocation();
    const {i18n} = useTranslation();
    const url = canonical || new URL(pathname, config.homeUrl).href;
    return (
        <>
            <meta property="og:url" content={url} />
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={config.title} />
            <meta property="og:locale" content={socialLocales[i18n.language] || socialLocales[DEFAULT_LANGUAGE]} />
            {alternateLanguages
                .filter((lang) => lang !== i18n.language && socialLocales[lang])
                .map((lang) => (
                    <meta key={lang} property="og:locale:alternate" content={socialLocales[lang]} />
                ))}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image.url} />
            {image.url.startsWith('https:') && <meta property="og:image:secure_url" content={image.url} />}
            <meta property="og:image:alt" content={image.alt} />
            {image.width && image.height && (
                <>
                    <meta property="og:image:width" content={image.width} />
                    <meta property="og:image:height" content={image.height} />
                </>
            )}
            {image.type && <meta property="og:image:type" content={image.type} />}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image.url} />
            <meta name="twitter:image:alt" content={image.alt} />
        </>
    );
};

const PageMeta = ({page, path, robots}) => {
    const {t, i18n} = useTranslation();
    const description = t(`pages.${page}.meta.description`);

    return (
        <>
            <title>{t(`pages.${page}.title`)}</title>
            <meta name="description" content={description} />
            {path !== undefined && seoLinks(path, i18n.language)}
            <meta name="robots" content={robots || 'index, follow, max-image-preview:large'} />
            {path !== undefined && (
                <SocialMeta
                    title={t(`pages.${page}.title`)}
                    description={description}
                    alternateLanguages={LANGUAGES.map(({code}) => code)}
                />
            )}
        </>
    );
};

export default PageMeta;
